import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { promotionApi, studentsApi } from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowUpRight, GraduationCap, Loader2, ShieldAlert, Check, RotateCcw, TrendingUp,
} from 'lucide-react';

const ACTION_STYLE = {
  promote: { variant: 'default', icon: ArrowUpRight, label: 'PROMOTE' },
  graduate: { variant: 'secondary', icon: GraduationCap, label: 'GRADUATE' },
  missing_target: { variant: 'outline', icon: ShieldAlert, label: 'SKIPPED (no target)' },
  unknown: { variant: 'outline', icon: ShieldAlert, label: 'SKIPPED' },
};

const BulkPromoteDialog = ({ open, onOpenChange, currentAcademicYear = '2026-27', onDone }) => {
  const [step, setStep] = useState('loading');   // loading | preview | detain | confirming | done
  const [preview, setPreview] = useState(null);
  const [newYear, setNewYear] = useState('');
  const [detainOpen, setDetainOpen] = useState(false);
  const [detainClass, setDetainClass] = useState(null);      // {class_id, class_name}
  const [detainStudents, setDetainStudents] = useState([]);
  const [detainedIds, setDetainedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const yearNext = (ay) => {
    const m = /^(\d{4})-(\d{2,4})$/.exec(ay);
    if (!m) return ay;
    const start = parseInt(m[1]) + 1;
    const end = (parseInt(m[2]) % 100) + 1;
    return `${start}-${String(end).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!open) {
      setStep('loading'); setPreview(null); setResult(null);
      setDetainedIds(new Set()); setDetainStudents([]); setDetainClass(null);
      return;
    }
    (async () => {
      try {
        const { data } = await promotionApi.preview(currentAcademicYear);
        setPreview(data);
        setNewYear(yearNext(currentAcademicYear));
        setStep('preview');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load preview');
        onOpenChange(false);
      }
    })();
  }, [open, currentAcademicYear, onOpenChange]);

  const openDetain = async (row) => {
    setDetainClass({ class_id: row.class_id, class_name: row.class_name });
    try {
      // Fetch students of this class
      const { data } = await studentsApi.getAll({ class_id: row.class_id, limit: 500 });
      const students = data.students || data.items || data || [];
      setDetainStudents(Array.isArray(students) ? students : []);
      setDetainOpen(true);
    } catch (e) {
      toast.error('Could not load class roster');
    }
  };

  const toggleDetain = (id) => {
    setDetainedIds(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const execute = async () => {
    if (!window.confirm(
      `Confirm promotion for ${currentAcademicYear} → ${newYear}?\n\n` +
      `• ${preview.total_promoting - detainedIds.size} students will be promoted\n` +
      `• ${preview.total_graduating} students will graduate (10th)\n` +
      `• ${detainedIds.size} students detained (kept in class)\n\n` +
      `A snapshot will be created — undo is available for 24h.`
    )) return;
    setBusy(true);
    try {
      const { data } = await promotionApi.execute({
        academic_year: currentAcademicYear,
        new_academic_year: newYear,
        detained_student_ids: Array.from(detainedIds),
      });
      setResult(data);
      setStep('done');
      onDone?.();
      toast.success(`Promoted ${data.promoted}, graduated ${data.graduated}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Promotion failed');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!result?.snapshot_id) return;
    if (!window.confirm('Undo this promotion? All students revert to their previous class.')) return;
    setBusy(true);
    try {
      const { data } = await promotionApi.undo(result.snapshot_id);
      toast.success(`Reverted ${data.reverted} students`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Bulk Promote Students
            {preview && step === 'preview' && (
              <Badge variant="outline" className="ml-2">Preview · {preview.total_classes} classes</Badge>
            )}
            {step === 'done' && <Badge className="ml-2 bg-green-600">Completed</Badge>}
          </DialogTitle>
        </DialogHeader>

        {step === 'loading' && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded border border-blue-200">
                <p className="text-xs text-muted-foreground">Promoting</p>
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {preview.total_promoting - detainedIds.size}
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded border border-green-200">
                <p className="text-xs text-muted-foreground">Graduating (10th)</p>
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {preview.total_graduating}
                </p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded border border-amber-200">
                <p className="text-xs text-muted-foreground">Detained (kept in class)</p>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                  {detainedIds.size}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Current Academic Year</Label>
                <Input value={preview.academic_year} disabled />
              </div>
              <div>
                <Label>New Academic Year</Label>
                <Input value={newYear} onChange={e => setNewYear(e.target.value)}
                       data-testid="promote-new-year" />
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Class</TableHead>
                  <TableHead className="text-center">Students</TableHead>
                  <TableHead></TableHead>
                  <TableHead>→ Target</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead className="text-right">Detain</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map(row => {
                  const style = ACTION_STYLE[row.action] || ACTION_STYLE.unknown;
                  const Icon = style.icon;
                  const detainedInThis = detainStudents.length && detainClass?.class_id === row.class_id
                    ? Array.from(detainedIds).filter(id => detainStudents.find(s => s.id === id)).length
                    : 0;
                  return (
                    <TableRow key={row.class_id} data-testid={`promote-row-${row.canonical_name}`}>
                      <TableCell className="font-medium">{row.class_name}</TableCell>
                      <TableCell className="text-center">{row.student_count}</TableCell>
                      <TableCell><ArrowUpRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.next_class_name || (row.action === 'graduate' ? '(graduate)' : '—')}
                      </TableCell>
                      <TableCell>
                        <Badge variant={style.variant} className="text-xs">
                          <Icon className="h-3 w-3 mr-1" />
                          {style.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {(row.action === 'promote' || row.action === 'graduate') && row.student_count > 0 && (
                          <Button size="sm" variant="ghost"
                                  onClick={() => openDetain(row)}
                                  data-testid={`promote-detain-${row.canonical_name}`}>
                            Detain {detainedInThis > 0 ? `(${detainedInThis})` : ''}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="bg-muted/50 rounded p-3 text-xs">
              <p className="flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <span>
                  This creates a snapshot — you can undo within <b>24 hours</b>.
                  All classes will move to <b>{newYear}</b>. Students in 10th become <b>graduated</b>
                  (kept as read-only records).
                </span>
              </p>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-4 text-center py-6">
            <div className="mx-auto w-14 h-14 rounded-full bg-green-100 dark:bg-green-950/40 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold">Promotion complete!</h3>
            <div className="grid grid-cols-3 gap-3 text-sm max-w-md mx-auto">
              <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded">
                <p className="text-xs text-muted-foreground">Promoted</p>
                <p className="text-2xl font-bold text-blue-700">{result.promoted}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded">
                <p className="text-xs text-muted-foreground">Graduated</p>
                <p className="text-2xl font-bold text-green-700">{result.graduated}</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded">
                <p className="text-xs text-muted-foreground">Detained</p>
                <p className="text-2xl font-bold text-amber-700">{result.detained}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Snapshot ID: <code>{result.snapshot_id?.slice(0, 8)}…</code> — undo available for 24h.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={execute} disabled={busy || !preview || preview.total_classes === 0}
                      data-testid="promote-execute-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TrendingUp className="h-4 w-4 mr-2" />}
                Promote {preview ? preview.total_promoting + preview.total_graduating - detainedIds.size : 0} students
              </Button>
            </>
          )}
          {step === 'done' && (
            <>
              <Button variant="outline" onClick={undo} disabled={busy} data-testid="promote-undo-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Undo
              </Button>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </>
          )}
        </DialogFooter>

        {/* Nested Detain dialog */}
        <Dialog open={detainOpen} onOpenChange={setDetainOpen}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detain students from {detainClass?.class_name}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Selected students will <b>stay in {detainClass?.class_name}</b> instead of being promoted.
            </p>
            <div className="max-h-96 overflow-y-auto border rounded divide-y">
              {detainStudents.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">No students in this class.</p>
              ) : detainStudents.map(s => (
                <label key={s.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 cursor-pointer">
                  <Checkbox checked={detainedIds.has(s.id)} onCheckedChange={() => toggleDetain(s.id)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground">Roll: {s.roll_number}</p>
                  </div>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={() => setDetainOpen(false)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPromoteDialog;
